/**
 * Customer document reads via Admin SDK Cloud Functions.
 * Bypasses browser security rules — same credential path as collection discovery.
 */
import {
  getActiveRuntimeConnectionId,
  getRuntimeDb,
  hasActiveRuntime,
} from "../features/firestoreStudio/runtime/runtimeDb";
import { queryCustomerDocuments, getCustomerCollectionCount } from "./adminApi";
import { formatCallableDiscoveryError } from "./collectionDiscoveryService";

function runtimeProjectId() {
  return getRuntimeDb()?.app?.options?.projectId || null;
}

function normalizeCursor(cursor) {
  if (!cursor) return null;
  if (cursor.docId) return { docId: cursor.docId, sortValue: cursor.sortValue ?? null };
  return null;
}

async function callQuery(payload) {
  try {
    const res = await queryCustomerDocuments(payload);
    return res.data;
  } catch (err) {
    const code = err?.code ? ` [${err.code}]` : "";
    throw new Error(
      `queryCustomerDocuments failed${code}: ${formatCallableDiscoveryError(err)}`
    );
  }
}

export async function adminFetchDocuments({
  collectionName,
  sortField = "id",
  sortAsc = true,
  pageLimit = 100,
  cursor = null,
  docIdQuery = "",
  fieldQuery = "",
}) {
  if (!hasActiveRuntime()) {
    throw new Error("No active workspace connection.");
  }
  const connectionId = getActiveRuntimeConnectionId();
  const data = await callQuery({
    connectionId,
    runtimeProjectId: runtimeProjectId(),
    collectionName,
    sortField,
    sortAsc,
    pageLimit,
    cursor: normalizeCursor(cursor),
    docIdQuery,
    fieldQuery,
  });

  return {
    rows: data.rows || [],
    hasMore: Boolean(data.hasMore),
    cursor: data.cursor || null,
    source: data.engine || "admin_sdk",
  };
}

export async function adminGetCollectionCount(collectionName) {
  if (!hasActiveRuntime()) {
    throw new Error("No active workspace connection.");
  }
  const connectionId = getActiveRuntimeConnectionId();
  try {
    const res = await getCustomerCollectionCount({
      connectionId,
      runtimeProjectId: runtimeProjectId(),
      collectionName,
    });
    return res.data?.count ?? 0;
  } catch (err) {
    const code = err?.code ? ` [${err.code}]` : "";
    throw new Error(
      `getCustomerCollectionCount failed${code}: ${formatCallableDiscoveryError(err)}`
    );
  }
}
